import { useCallback, useEffect, useRef } from 'react';

interface VideoInstance {
  pause: () => void;
}

// 全局视频管理器
class VideoManager {
  private videos: Map<string, VideoInstance> = new Map();

  /**
   * 注册视频实例
   */
  registerVideo(id: string, pause: () => void) {
    this.videos.set(id, { pause });
  }

  /**
   * 注销视频实例
   */
  unregisterVideo(id: string) {
    this.videos.delete(id);
  }

  /**
   * 暂停所有其他视频
   */
  pauseOtherVideos(currentId: string) {
    this.videos.forEach((video, id) => {
      if (id !== currentId) {
        video.pause();
      }
    });
  }
}

// 全局单例
const globalVideoManager = new VideoManager();

/**
 * 视频播放管理 Hook
 * 用于管理多个视频播放，确保同时只有一个视频播放
 */
export function useVideoManager() {
  // 生成唯一的视频 ID（仅在首次渲染时生成）
  const videoId = useRef(`video-${Math.random()}-${Date.now()}`).current;
  // 记录是否已注册
  const isRegistered = useRef(false);

  /**
   * 注册暂停处理器并暂停其他视频
   */
  const registerAndPauseOthers = useCallback(
    (player: any) => {
      // 避免重复注册
      if (!isRegistered.current) {
        globalVideoManager.registerVideo(videoId, () => {
          player?.pause();
        });
        isRegistered.current = true;
      }
      // 暂停其他视频
      globalVideoManager.pauseOtherVideos(videoId);
    },
    [videoId],
  );

  /**
   * 暂停其他视频（不需要 player 实例）
   */
  const pauseOthersOnPlay = useCallback(() => {
    // 暂停其他视频
    globalVideoManager.pauseOtherVideos(videoId);
  }, [videoId]);

  /**
   * 清理
   */
  useEffect(() => {
    return () => {
      globalVideoManager.unregisterVideo(videoId);
      isRegistered.current = false;
    };
  }, [videoId]);

  return {
    registerAndPauseOthers,
    pauseOthersOnPlay,
  };
}
